/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import apiClient from './api';
import type {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  RegisterTenantRequest,
  RegisterTenantResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  RefreshTokenResponse,
} from '../types';

export const authService = {
  /**
   * Login user
   */
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/login', data);
    if (response.data.success && response.data.data) {
      // Tokens are now stored in httpOnly cookies by the server
      // Only store user data in localStorage for UI purposes
      const userData = response.data.data.user || response.data.data;
      localStorage.setItem('user', JSON.stringify(userData));

      // Return user data with mock tokens for compatibility
      // (actual tokens are in httpOnly cookies)
      return {
        user: userData,
        accessToken: '', // Not needed in localStorage anymore
        refreshToken: '', // Not needed in localStorage anymore
      } as LoginResponse;
    }
    throw new Error(response.data.message || 'Login failed');
  },

  /**
   * Logout user
   */
  logout: async (): Promise<void> => {
    try {
      // This will clear the httpOnly cookies on the server
      await apiClient.post('/auth/logout');
    } finally {
      // Clear user data from local storage
      // Tokens are already cleared by server (httpOnly cookies)
      localStorage.removeItem('user');
    }
  },

  /**
   * Register new tenant
   */
  registerTenant: async (data: RegisterTenantRequest): Promise<RegisterTenantResponse> => {
    const response = await apiClient.post<ApiResponse<RegisterTenantResponse>>(
      '/auth/register',
      data
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Registration failed');
  },

  /**
   * Refresh access token
   */
  refreshToken: async (): Promise<RefreshTokenResponse> => {
    // Refresh token is now in httpOnly cookie, no need to pass it
    const response = await apiClient.post<ApiResponse<RefreshTokenResponse>>('/auth/refresh');
    if (response.data.success) {
      // Tokens are now set in httpOnly cookies by the server
      return {
        accessToken: '', // Not needed in localStorage
        refreshToken: '', // Not needed in localStorage
      };
    }
    throw new Error(response.data.message || 'Token refresh failed');
  },

  /**
   * Request password reset
   */
  forgotPassword: async (data: ForgotPasswordRequest): Promise<void> => {
    const response = await apiClient.post<ApiResponse>('/auth/forgot-password', data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Password reset request failed');
    }
  },

  /**
   * Reset password with token
   */
  resetPassword: async (data: ResetPasswordRequest): Promise<void> => {
    const response = await apiClient.post<ApiResponse>('/auth/reset-password', data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Password reset failed');
    }
  },

  /**
   * Change password (authenticated user)
   */
  changePassword: async (data: ChangePasswordRequest): Promise<void> => {
    const response = await apiClient.post<ApiResponse>('/auth/change-password', data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Password change failed');
    }
  },

  /**
   * Accept invitation
   */
  acceptInvitation: async (data: AcceptInvitationRequest): Promise<AcceptInvitationResponse> => {
    const response = await apiClient.post<ApiResponse<AcceptInvitationResponse>>(
      '/auth/accept-invitation',
      data
    );
    if (response.data.success && response.data.data) {
      // Tokens are now stored in httpOnly cookies by the server
      // Only store user data in localStorage for UI purposes
      const userData = response.data.data.user || response.data.data;
      localStorage.setItem('user', JSON.stringify(userData));

      return {
        user: userData,
        accessToken: '', // Not needed in localStorage
        refreshToken: '', // Not needed in localStorage
      } as AcceptInvitationResponse;
    }
    throw new Error(response.data.message || 'Invitation acceptance failed');
  },

  /**
   * Exchange a one-time login token for a session (mobile → web SSO)
   */
  exchangeLoginToken: async (loginToken: string): Promise<LoginResponse> => {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/exchange-token', { loginToken });
    if (response.data.success && response.data.data) {
      const userData = response.data.data.user || response.data.data;
      localStorage.setItem('user', JSON.stringify(userData));
    }
    return response.data.data!;
  },

  /**
   * Get current user from localStorage
   */
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  /**
   * Check if user is authenticated
   * With httpOnly cookies, we check if user data exists in localStorage
   * The actual token validity is checked on the server
   */
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('user');
  },
};
